/** Domain vocabulary, mirroring the API's enums.
 *
 * Kept in one file so a label change happens once. The values must match
 * `wheelerpmt/db/models/project.py` exactly — they travel over the wire.
 */

/** The four stages, in the order they run.
 *
 * Each carries a one-line meaning, shown wherever a stage is being chosen. Nobody should have
 * to infer what "Execution & Transition" covers from its name, and a stage picked because the
 * label sounded closest is worse than no stage at all.
 */
export const STAGES = [
  {
    value: 'initiation',
    label: 'Initiation',
    description:
      'The idea is agreed and somebody owns it. What it will take is still being worked out.',
  },
  {
    value: 'planning_design',
    label: 'Planning & Design',
    description:
      'Deciding what will be done, how, and by when. The charter and the rollout plan get written here.',
  },
  {
    value: 'execution_transition',
    label: 'Execution & Transition',
    description:
      'The work is being done and handed over to operations. Most projects spend most of their life here.',
  },
  {
    value: 'closure',
    label: 'Closure',
    description:
      'Work is finished and handed over. Completing a project needs a closure record and a resolved runbook.',
  },
] as const

export const stageDescription = (value: string | undefined): string =>
  STAGES.find((s) => s.value === value)?.description ?? ''

export const STATUSES = [
  { value: 'not_started', label: 'Not started' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
] as const

export const RAG = [
  { value: 'none', label: 'No RAG' },
  { value: 'green', label: 'Green' },
  { value: 'amber', label: 'Amber' },
  { value: 'red', label: 'Red' },
] as const

const labelOf = (
  options: readonly { value: string; label: string }[],
  value: string | undefined,
): string => options.find((o) => o.value === value)?.label ?? value ?? '—'

export const stageLabel = (value: string | undefined) => labelOf(STAGES, value)
export const statusLabel = (value: string | undefined) => labelOf(STATUSES, value)

/** The three delivery teams.
 *
 * The API returns a label with every project and every share page, and that is the source
 * of truth. This list exists only for grouping keys, where a view holds the stored value
 * and nothing else. Keep it in step with `TEAM_LABELS` in `wheelerpmt/db/models/project.py`.
 *
 * The old `WORKSTREAMS` list lived here too and was deleted on 2026-08-13. It still carried
 * "Business Intelligence" and "Software Development" long after both were renamed, and the
 * share pages rendered it under a heading that read "Team". A second list of names is a
 * second thing to forget. */
export const TEAMS = [
  { value: 'it_operations', label: 'IT Operations' },
  { value: 'enterprise_data_enablement_engineering', label: 'Enterprise Data Enablement & Engineering' },
  { value: 'software_development', label: 'Software Development & AI' },
  { value: 'unassigned', label: 'Unassigned' },
] as const
export const teamLabel = (value: string | undefined) => labelOf(TEAMS, value)

export type Tone = 'neutral' | 'green' | 'amber' | 'red' | 'info' | 'orange'

export const ragTone = (value: string | undefined): Tone =>
  value === 'green' ? 'green' : value === 'amber' ? 'amber' : value === 'red' ? 'red' : 'neutral'

export const statusTone = (value: string | undefined): Tone =>
  value === 'active'
    ? 'info'
    : value === 'complete'
      ? 'green'
      : value === 'on_hold'
        ? 'amber'
        : value === 'cancelled'
          ? 'orange'
          : 'neutral'

/** The API's project shape. */
export interface Project {
  id: string
  name: string
  description: string
  stage: string
  status: string
  ragStatus: string
  /** Superseded by team + category; still returned by the API so nothing breaks mid-deploy. */
  workstream: string
  team: string
  teamLabel: string
  teamShortLabel: string
  categoryId: string | null
  ownerName: string
  ownerUserId: string | null
  startDate: string | null
  targetDate: string | null
  actualEndDate: string | null
  /** When an on-hold project is expected to pick back up. Null unless on hold. */
  onHoldUntil: string | null
  percentComplete: number
  /** True when a person typed the figure instead of it being rolled up from work items. */
  percentIsManual: boolean
  scores: {
    value: number | null
    risk: number | null
    effort: number | null
    /** (Value + Risk) / Effort. Null when unscored — render "unscored", never zero. */
    priority: number | null
  }
  rank: number | null
  /** How far the target date has moved since it was first set. Null when it never has. */
  targetMovement: { moves: number; days: number } | null
  rankOverride: number | null
  rankOverrideReason: string | null
  notes: string
  closure: { summary: string | null; handoffNote: string | null }
  /** Mirrors an Azure DevOps epic. Name, team and category are managed by the sync
   * (team and category from the ADO Sync tab); everything else stays editable here. */
  /** Run as a Wildly Important Goal. The WIG itself lives on the WIG board. */
  isWig: boolean
  /** The one initiative this project belongs to, if any. */
  initiativeId: string | null
  isExternal: boolean
  externalSource: string | null
  externalUrl: string | null
  rowVersion: number
  modifiedDate: string
  modifiedBy: string
}

export const formatDate = (value: string | null): string =>
  value
    ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—'

/** "On hold until 1 Jan 2027" — beside the status wherever the status is shown. */
export function onHoldNote(project: Pick<Project, 'status' | 'onHoldUntil'>): string {
  return project.status === 'on_hold' && project.onHoldUntil
    ? `until ${formatDate(project.onHoldUntil)}`
    : ''
}
